import { useCallback, useEffect, useMemo, useState } from "react";
import { Text, Spinner, Alert, AlertIcon } from "@chakra-ui/react";
import { FiRotateCcw, FiSave } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { useUnitsAndMeasures } from "newui/store";
import { showToast } from "utils/helpers";

import type {
	ScheduleColumn,
	ScheduleColumnType,
} from "./schedule-of-rates.types";
import {
	getCompanyScheduleOfRates,
	saveCompanyScheduleOfRates,
} from "../../api";
import {
	SpreadsheetGrid,
	useColumnManager,
} from "../SpreadsheetEditor";
import type {
	ColumnConfig,
	QuoteColumnType,
} from "../SpreadsheetEditor";
import {
	ALLOWED_QUOTE_TYPES,
	quoteToScheduleType,
	scheduleToQuoteType,
	SCHEDULE_DEFAULT_COLUMNS,
} from "./schedule-column-type-mapping";
import { formatCurrencyValue, reKeyCellsBySortOrder } from "./schedule-utils";
import { useUndoStack } from "../QuoteTable/hooks/useUndoStack";
import {
	ScheduleRowsBody,
	type ScheduleRowDraft,
} from "./components/ScheduleRowsBody";

export interface CompanyScheduleSettingsProps {
	companyId: number;
}

interface DraftSnapshot {
	columns: ColumnConfig[];
	rows: ScheduleRowDraft[];
}

const generateLocalRowId = (): number =>
	-Math.floor(Math.random() * 1_000_000_000);

const buildLocalId = (prefix: string, seed: string | number): string =>
	`${prefix}-${seed}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Default column set when a company has no saved schedule yet. Sourced from
 * the shared SCHEDULE_DEFAULT_COLUMNS (schedule-column-type-mapping.ts) so
 * this settings page and EditScheduleOfRatesModal can't drift apart on
 * labels/types — only localId generation is specific to this call site.
 * Bypassing the generic useColumnManager defaults (which include Quantity +
 * Cost — both unsupported by Schedule of Rates) — those would render with
 * "Not supported" disabled reasons and confuse the user.
 */
const buildScheduleDefaultColumns = (): ColumnConfig[] =>
	SCHEDULE_DEFAULT_COLUMNS.map((col, idx) => ({
		...col,
		localId: buildLocalId("sched-default", idx),
	}));

/**
 * Convert a persisted ScheduleColumn[] (numeric ids, schedule types) into the
 * SpreadsheetEditor's ColumnConfig[] (string localIds, quote types) and
 * produce the lookup needed to re-key incoming row values.
 */
const adoptColumns = (
	source: ScheduleColumn[]
): { idToLocalId: Map<number, string>; columns: ColumnConfig[]; } => {
	const idToLocalId = new Map<number, string>();
	const columns: ColumnConfig[] = source.map((c, idx) => {
		const localId = buildLocalId(`sched-col-${c.id}`, idx);
		idToLocalId.set(c.id, localId);
		return {
			id: c.id,
			columnType: scheduleToQuoteType(c.columnType),
			label: c.label,
			localId,
			sortOrder: c.sortOrder,
		};
	});
	return { columns, idToLocalId };
};

const CompanyScheduleSettings: React.FC<CompanyScheduleSettingsProps> = ({
	companyId,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [saveSuccess, setSaveSuccess] = useState(false);

	const [rows, setRows] = useState<ScheduleRowDraft[]>([]);
	const [validationAttempted, setValidationAttempted] = useState(false);

	const { values: unitsAndMeasures } = useUnitsAndMeasures();

	const {
		addColumn,
		columns,
		duplicateColumn,
		executeColumnDelete,
		getDisabledReason: hookGetDisabledReason,
		insertColumn,
		isTypeDisabled: hookIsTypeDisabled,
		moveColumn,
		pendingDeleteId,
		removeColumn,
		reorderColumns,
		resetForEdit,
		updateColumnLabel,
		updateColumnType,
	} = useColumnManager(buildScheduleDefaultColumns());

	// -----------------------------------------------------------------------
	// Schedule has a narrower type set than the generic SpreadsheetEditor —
	// constrain the type chooser at the boundary.
	// -----------------------------------------------------------------------

	const isTypeDisabled = useCallback(
		(type: QuoteColumnType, currentColType?: QuoteColumnType) => {
			if (!ALLOWED_QUOTE_TYPES.has(type)) return true;
			return hookIsTypeDisabled(type, currentColType);
		},
		[hookIsTypeDisabled]
	);

	const getDisabledReason = useCallback(
		(type: QuoteColumnType) => {
			if (!ALLOWED_QUOTE_TYPES.has(type)) {
				return "Not supported in Schedule of Rates";
			}
			return hookGetDisabledReason(type);
		},
		[hookGetDisabledReason]
	);

	// Hide unsupported types from the chooser entirely — Schedule of Rates only
	// surfaces DESC / UOM / VALUE / NUMBER. Disabling them isn't enough; the user
	// shouldn't see options that can never be picked.
	const isTypeHidden = useCallback(
		(type: QuoteColumnType) => !ALLOWED_QUOTE_TYPES.has(type),
		[]
	);

	// -----------------------------------------------------------------------
	// Auto-confirm column deletes — matches the template editor UX.
	// -----------------------------------------------------------------------

	useEffect(() => {
		if (pendingDeleteId !== null) {
			executeColumnDelete(pendingDeleteId);
		}
	}, [pendingDeleteId, executeColumnDelete]);

	// -----------------------------------------------------------------------
	// Load — normalise persisted data so row values are keyed by localId.
	// -----------------------------------------------------------------------

	const fetchSchedule = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getCompanyScheduleOfRates(companyId);
			if (data && data.columns.length > 0) {
				const { columns: cols, idToLocalId } = adoptColumns(data.columns);
				resetForEdit(cols);
				setRows(
					data.rows.map((r) => {
						const values: Record<string, string> = {};
						for (const [colIdStr, val] of Object.entries(r.values)) {
							const localId = idToLocalId.get(Number(colIdStr));
							if (localId) values[localId] = val;
						}
						return { id: r.id, sortOrder: r.sortOrder, values };
					})
				);
			}
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsLoading(false);
		}
	}, [companyId, resetForEdit]);

	useEffect(() => {
		fetchSchedule();
	}, [fetchSchedule]);

	// -----------------------------------------------------------------------
	// Row mutations
	// -----------------------------------------------------------------------

	const handleAddRow = useCallback(() => {
		setRows((prev) => [
			...prev,
			{ id: generateLocalRowId(), sortOrder: prev.length + 1, values: {} },
		]);
	}, []);

	const handleRemoveRow = useCallback((rowId: number) => {
		setRows((prev) =>
			prev
				.filter((r) => r.id !== rowId)
				.map((r, i) => ({ ...r, sortOrder: i + 1 }))
		);
	}, []);

	const handleChangeCell = useCallback(
		(rowId: number, columnLocalId: string, value: string) => {
			setRows((prev) =>
				prev.map((r) =>
					r.id === rowId
						? { ...r, values: { ...r.values, [columnLocalId]: value } }
						: r
				)
			);
		},
		[]
	);

	const handleBlurCurrency = useCallback(
		(rowId: number, columnLocalId: string) => {
			setRows((prev) =>
				prev.map((r) => {
					if (r.id !== rowId) return r;
					const raw = r.values[columnLocalId] ?? "";
					if (!raw) return r;
					return {
						...r,
						values: {
							...r.values,
							[columnLocalId]: formatCurrencyValue(raw),
						},
					};
				})
			);
		},
		[]
	);

	const handleReorderRows = useCallback(
		(sourceIndex: number, destIndex: number) => {
			setRows((prev) => {
				if (
					sourceIndex < 0 ||
					destIndex < 0 ||
					sourceIndex >= prev.length ||
					destIndex >= prev.length
				)
					return prev;
				const next = [...prev];
				const [moved] = next.splice(sourceIndex, 1);
				next.splice(destIndex, 0, moved);
				return next.map((r, i) => ({ ...r, sortOrder: i + 1 }));
			});
		},
		[]
	);

	// -----------------------------------------------------------------------
	// Undo stack (same hook + capacity 5 / 400ms debounce as templates)
	// -----------------------------------------------------------------------

	const currentSnapshot = useMemo<DraftSnapshot>(
		() => ({ columns, rows }),
		[columns, rows]
	);

	const { canUndo, undo, suppressNext } = useUndoStack<DraftSnapshot>({
		current: currentSnapshot,
		enabled: !isLoading,
	});

	const handleUndo = useCallback(() => {
		const snapshot = undo();
		if (!snapshot) return;
		suppressNext();
		resetForEdit(snapshot.columns.map((c) => ({ ...c })));
		setRows(
			snapshot.rows.map((r) => ({ ...r, values: { ...r.values } }))
		);
	}, [undo, suppressNext, resetForEdit]);

	useEffect(() => {
		if (isLoading) return;
		const handler = (e: KeyboardEvent) => {
			const isUndo =
				(e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "z";
			if (!isUndo) return;
			const target = e.target as HTMLElement | null;
			const tag = target?.tagName?.toLowerCase();
			if (tag === "input" || tag === "textarea" || tag === "select") return;
			e.preventDefault();
			handleUndo();
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [isLoading, handleUndo]);

	// -----------------------------------------------------------------------
	// Validation — once the user attempts save, highlight any empty cell.
	// -----------------------------------------------------------------------

	const cellErrors = useMemo<Map<number, Set<string>>>(() => {
		if (!validationAttempted) return new Map();
		const result = new Map<number, Set<string>>();
		if (rows.length === 0 || columns.length === 0) return result;
		for (const row of rows) {
			const missing = new Set<string>();
			for (const col of columns) {
				const v = (row.values[col.localId] ?? "").trim();
				if (!v) missing.add(col.localId);
			}
			if (missing.size > 0) result.set(row.id, missing);
		}
		return result;
	}, [validationAttempted, rows, columns]);

	const unitOptions = useMemo(
		() =>
			(unitsAndMeasures ?? []).map((u) => ({
				label: u.label,
				value: u.value,
			})),
		[unitsAndMeasures]
	);

	// -----------------------------------------------------------------------
	// Save
	// -----------------------------------------------------------------------

	const handleSave = useCallback(async () => {
		setValidationAttempted(true);

		// Inline validation duplicated here so we can early-return without
		// waiting for cellErrors memo to update on the next render.
		if (rows.length > 0) {
			for (const row of rows) {
				for (const col of columns) {
					if (!(row.values[col.localId] ?? "").trim()) {
						showToast(
							"Please fill in all fields before saving",
							"Error",
							false
						);
						return;
					}
				}
			}
		}

		// Assign sequential negative ids to columns that don't yet have one, then
		// map row values from column-localId to numeric column id. That id is
		// only a stepping stone — the `columns` table has no id column, so
		// what's actually saved must be re-keyed to sort_order (see
		// reKeyCellsBySortOrder) or a freshly-added column's cells become
		// unreachable on reload.
		let nextNegId = -1;
		const localIdToFinalId = new Map<string, number>();
		const columnsForCells: { id: number; sortOrder: number }[] = [];
		const apiColumns = columns.map((c) => {
			const finalId = c.id ?? nextNegId--;
			localIdToFinalId.set(c.localId, finalId);
			columnsForCells.push({ id: finalId, sortOrder: c.sortOrder });
			const scheduleType: ScheduleColumnType = quoteToScheduleType(
				c.columnType
			);
			return {
				name: c.label,
				type: scheduleType,
				sort_order: c.sortOrder,
			};
		});

		const apiRows = rows.map((r) => {
			const valuesByFinalId: Record<number, string> = {};
			for (const [localId, value] of Object.entries(r.values)) {
				const finalId = localIdToFinalId.get(localId);
				if (finalId !== undefined) valuesByFinalId[finalId] = value;
			}
			return {
				sort_order: r.sortOrder,
				cells: reKeyCellsBySortOrder(columnsForCells, valuesByFinalId),
			};
		});

		setIsSaving(true);
		setSaveSuccess(false);
		try {
			await saveCompanyScheduleOfRates(companyId, {
				name: "Company Schedule of Rates",
				columns: apiColumns,
				rows: apiRows,
			});
			setSaveSuccess(true);
			setValidationAttempted(false);
			showToast("Schedule of rates saved successfully", "Success", true);
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsSaving(false);
		}
	}, [companyId, columns, rows]);

	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center py-12"
				data-testid="company-schedule-loading"
			>
				<Spinner size="lg" />
			</div>
		);
	}

	const hasContent = columns.length > 0;

	return (
		<div data-testid="company-schedule-settings">
			<div className="mb-4 flex items-center justify-between">
				<div>
					<Text className="font-roboto text-lg font-semibold text-gray-900">
						Schedule of Rates
					</Text>
					<Text className="text-sm text-gray-500">
						Define your company&apos;s default schedule of rates template. This
						will be used as the starting point when adding a schedule to new
						submissions.
					</Text>
				</div>
				<div className="flex items-center gap-2">
					<Button
						size="sm"
						btntype="secondary"
						onClick={handleUndo}
						disabled={!canUndo}
						aria-label="Undo last change"
						data-testid="undo-schedule-btn"
					>
						<FiRotateCcw className="mr-1" />
						Undo
					</Button>
					<Button
						size="sm"
						btntype="primary"
						onClick={handleSave}
						disabled={isSaving || !hasContent}
						data-testid="save-company-schedule-btn"
					>
						<FiSave className="mr-1" />
						{isSaving ? "Saving..." : "Save"}
					</Button>
				</div>
			</div>

			{saveSuccess && (
				<Alert
					status="success"
					className="mb-4 rounded-md"
					data-testid="save-success-alert"
				>
					<AlertIcon />
					<Text className="text-sm">
						Schedule of rates template saved successfully.
					</Text>
				</Alert>
			)}

			{!hasContent && (
				<div className="mb-3 flex flex-col items-center gap-3 rounded-xl border border-dashed border-[#D0D5DD] bg-white p-6">
					<Text className="text-sm text-gray-500">
						No columns yet — add your first column to start building the
						schedule.
					</Text>
					<Button
						size="sm"
						btntype="secondary"
						onClick={addColumn}
						data-testid="schedule-add-first-column-btn"
					>
						Add column
					</Button>
				</div>
			)}

			{hasContent && (
				<div data-testid="schedule-inline-editor">
					<SpreadsheetGrid
						columns={columns}
						getDisabledReason={getDisabledReason}
						isTypeDisabled={isTypeDisabled}
						isTypeHidden={isTypeHidden}
						onAddColumn={addColumn}
						onDuplicateColumn={duplicateColumn}
						onInsertColumn={insertColumn}
						onMoveColumn={moveColumn}
						onRemoveColumn={removeColumn}
						onReorderColumns={reorderColumns}
						onUpdateLabel={updateColumnLabel}
						onUpdateType={updateColumnType}
						renderBody={(cols) => (
							<ScheduleRowsBody
								columns={cols}
								rows={rows}
								unitOptions={unitOptions}
								cellErrors={cellErrors}
								onAddRow={handleAddRow}
								onChangeCell={handleChangeCell}
								onBlurCurrency={handleBlurCurrency}
								onRemoveRow={handleRemoveRow}
								onReorderRows={handleReorderRows}
							/>
						)}
					/>
				</div>
			)}
		</div>
	);
};

export { CompanyScheduleSettings };
